<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import { db } from "../db/index.js";
    import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";
    import { getResourceTypeLabel } from "@edu-quizz/shared/constants/resourceTypes.js";

    export let params = {};

    let toc = null;
    let loading = true;
    let error = null;

    async function loadToc() {
        loading = true;
        error = null;
        try {
            const tocId = parseInt(params.id);
            toc = await db.getToc(tocId);
            if (!toc) {
                error = "Table des matières non trouvée";
            }
        } catch (err) {
            error = err.message;
        } finally {
            loading = false;
        }
    }

    async function loadResourceName(resourceServerId, resourceType) {
        try {
            switch (resourceType) {
                case RESOURCE_TYPES.QUIZ: {
                    const q = await db.getQuiz(resourceServerId);
                    return q?.name || null;
                }
                case RESOURCE_TYPES.FLASHCARD_DECK: {
                    const d = await db.getFlashcardDeck(resourceServerId);
                    return d?.name || null;
                }
                case RESOURCE_TYPES.ARTICLE: {
                    const a = await db.getArticle(resourceServerId);
                    return a?.name || null;
                }
                default:
                    return null;
            }
        } catch {
            return null;
        }
    }

    function navigateToResource(resourceServerId, resourceType) {
        switch (resourceType) {
            case RESOURCE_TYPES.QUIZ:
                push(`/quiz/${resourceServerId}`);
                break;
            case RESOURCE_TYPES.FLASHCARD_DECK:
                push(`/flashcard/${resourceServerId}`);
                break;
            case RESOURCE_TYPES.ARTICLE:
                push(`/article/${resourceServerId}`);
                break;
        }
    }

    onMount(() => {
        loadToc();
    });
</script>

<div class="toc-page">
    <div class="toc-header">
        <button class="btn btn-ghost btn-sm" on:click={() => push("/")}>
            ← Retour
        </button>
    </div>

    {#if loading}
        <div class="loading">Chargement...</div>
    {:else if error}
        <div class="error">{error}</div>
    {:else if toc}
        <h1>{toc.name}</h1>
        {#if toc.description}
            <p class="description">{toc.description}</p>
        {/if}

        {#each toc.themes || [] as theme}
            <div class="theme-section">
                <h2>{theme.name}</h2>
                {#if theme.description}
                    <p class="section-desc">{theme.description}</p>
                {/if}

                {#each theme.parts || [] as part}
                    <div class="part-section">
                        <h3>{part.name}</h3>
                        {#if part.description}
                            <p class="section-desc">{part.description}</p>
                        {/if}

                        {#each part.chapters || [] as chapter}
                            <div class="chapter-section">
                                <h4>{chapter.name}</h4>
                                {#if chapter.description}
                                    <p class="section-desc">{chapter.description}</p>
                                {/if}

                                <ul class="chapter-resources">
                                    {#each chapter.resources || [] as cr}
                                        {#await loadResourceName(cr.resourceServerId, cr.resourceType) then name}
                                            <li
                                                class="resource-item"
                                                on:click={() => navigateToResource(cr.resourceServerId, cr.resourceType)}
                                                role="button"
                                                tabindex="0"
                                            >
                                                <span class="badge badge-primary badge-sm">
                                                    {getResourceTypeLabel(cr.resourceType)}
                                                </span>
                                                <span class="resource-name">
                                                    {name || `#${cr.resourceServerId}`}
                                                </span>
                                            </li>
                                        {/await}
                                    {/each}
                                </ul>
                            </div>
                        {/each}
                    </div>
                {/each}
            </div>
        {/each}
    {/if}
</div>

<style>
    .toc-page {
        padding: 1.5rem;
        text-align: left;
    }

    .toc-header {
        margin-bottom: 1rem;
    }

    h1 {
        font-size: 1.5rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
    }

    .description {
        color: #666;
        margin-bottom: 1.5rem;
    }

    .theme-section {
        margin-bottom: 2rem;
        border-left: 4px solid var(--color-primary, #6366f1);
        padding-left: 1rem;
    }

    h2 {
        font-size: 1.2rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
    }

    .part-section {
        margin: 1rem 0;
        padding-left: 1rem;
        border-left: 2px solid rgba(0,0,0,0.1);
    }

    h3 {
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.25rem;
    }

    .chapter-section {
        margin: 0.75rem 0;
        padding-left: 1rem;
    }

    h4 {
        font-size: 0.9rem;
        font-weight: 600;
        color: #555;
        margin-bottom: 0.25rem;
    }

    .section-desc {
        font-size: 0.85rem;
        color: #888;
        margin-bottom: 0.5rem;
    }

    .chapter-resources {
        list-style: none;
        padding: 0;
        margin: 0.5rem 0;
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
    }

    .resource-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border: 1px solid rgba(0,0,0,0.1);
        border-radius: 0.5rem;
        cursor: pointer;
        transition: background 0.15s;
    }

    .resource-item:hover {
        background: var(--color-base-200, #f3f4f6);
    }

    .resource-name {
        font-size: 0.9rem;
    }

    .loading {
        text-align: center;
        padding: 3rem;
        color: #666;
    }

    .error {
        color: #c33;
        padding: 1rem;
        border: 1px solid #fcc;
        border-radius: 6px;
        background: #fee;
    }
</style>
